import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../lib/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is admin (has Admin role or is super admin)
  const isAdmin = user?.roles?.includes("Admin") || user?.is_super_admin;

  // Check if user is super admin
  const isSuperAdmin = user?.is_super_admin;

  // Check if user has specific role
  const hasRole = (roleName) => {
    return user?.roles?.includes(roleName) || user?.is_super_admin;
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Prefer session token first (non-remembered logins), then fallback to persistent token
        const token = sessionStorage.getItem("token") || localStorage.getItem("token");
        if (token) {
          api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          const response = await api.get("/auth/me");
          setUser(response.data.data);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        // Clear invalid tokens from both stores
        sessionStorage.removeItem("token");
        localStorage.removeItem("token");
        delete api.defaults.headers.common["Authorization"];
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password, rememberMe) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      const { user: userData, token } = response.data.data;

      if (rememberMe) {
        localStorage.setItem("token", token);
      } else {
        sessionStorage.setItem("token", token);
      }

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(userData);

      return response.data;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      const { user: newUser, token } = response.data.data;

      localStorage.setItem("token", token);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(newUser);

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Clear tokens from both storage locations
    sessionStorage.removeItem("token");
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  };

  const value = {
    user,
    loading,
    isAdmin,
    isSuperAdmin,
    hasRole,
    login,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
